/** Onboarding: welcome → connect Google Calendar → working hours → AI (info) → plan. Saves real preferences. */
import { sendSelfReport } from "../lib/acquisition";
import { httpsCallable } from "firebase/functions";
import { $$, byId, on, toast } from "../lib/dom";
import { functions } from "../lib/firebase";
import { savePreferences } from "../data/store";
import { state } from "../state";
import type { Preferences } from "../data/types";
import { renderConnections } from "./connections";

const syncCalendarNow = httpsCallable<Record<string, never>, { upserts: number; removed: number }>(functions, "syncCalendarNow");
const googleAuthUrl = httpsCallable<{ origin: string }, { url: string }>(functions, "googleAuthUrl");
const disconnectGoogle = httpsCallable<Record<string, never>, { ok: boolean }>(functions, "disconnectGoogle");
let step = 0;
let userNameForConnections = "you";
export const setConnectionsUser = (n: string) => { userNameForConnections = n; };
let current: Preferences | null = null; // prefs as last rendered, so async events can refresh the step UI

export async function runCalendarSync(silent = false) {
  try {
    const { data } = await syncCalendarNow({});
    if (!silent) toast(`Calendar synced · ${data.upserts} events`);
    return true;
  } catch (err) {
    // Self-heal: a workspace can carry "connected" from the old popup flow with no token on the server.
    const msg = (err as Error).message ?? "";
    if (/connect google calendar first/i.test(msg) && state.prefs.calendarConnected) {
      await savePreferences({ calendarConnected: false });
      if (!silent) toast("Google Calendar needs connecting again. Timo now stores the connection server-side.");
      return false;
    }
    if (!silent) toast(msg);
    return false;
  }
}

/** Sends the user to Google for offline calendar access; Google returns to /api/google/callback → back here with ?calendar=connected. */
export async function connectCalendar() {
  if (!state.setup.calendarConfigured) { toast("Google Calendar isn't set up for this workspace yet. An admin needs to add the OAuth client secret in Admin → Product."); return; }
  const btn = byId("connectCalendar");
  btn?.classList.add("busy");
  try {
    sessionStorage.setItem("timo-return-onboarding", byId("onboarding")?.classList.contains("show") ? String(step) : "");
    const { data } = await googleAuthUrl({ origin: location.origin });
    location.assign(data.url);
  } catch (err) {
    btn?.classList.remove("busy");
    toast((err as Error).message);
  }
}

export async function disconnectCalendar() {
  if (!confirm("Disconnect Google Calendar? Mirrored events are removed from Timo; nothing is deleted from Google.")) return;
  try { await disconnectGoogle({}); await savePreferences({ calendarConnected: false }); toast("Google Calendar disconnected"); }
  catch (err) { toast((err as Error).message); }
}

/** Called once on boot: turns the ?calendar= return parameter into feedback and resumes onboarding where it left off. */
export function handleCalendarReturn(prefs: Preferences) {
  const q = new URLSearchParams(location.search);
  const result = q.get("calendar");
  if (!result) return false;
  q.delete("calendar"); q.delete("reason");
  history.replaceState(null, "", `${location.pathname}${q.size ? `?${q}` : ""}${location.hash}`);
  const resume = sessionStorage.getItem("timo-return-onboarding"); sessionStorage.removeItem("timo-return-onboarding");
  if (result === "connected") { toast("Google Calendar connected · syncing"); if (!prefs.calendarConnected) void savePreferences({ calendarConnected: true }); }
  else if (result === "denied") toast("Google access was declined. You can connect later from Settings");
  else toast(`Couldn't connect Google Calendar (${new URLSearchParams(location.search).get("reason") ?? "try again"})`);
  if (resume !== null && resume !== "") { showOnboarding({ ...prefs, calendarConnected: prefs.calendarConnected || result === "connected" }, Number(resume)); return true; }
  return false;
}

function render(prefs: Preferences) {
  current = prefs;
  const steps = $$(".onboard-step");
  steps.forEach((s, i) => s.classList.toggle("active", i === step));
  $$(".progress-step").forEach((s, i) => { s.classList.toggle("active", i === step); s.classList.toggle("done", i < step); const ic = s.querySelector("i"); if (ic) ic.textContent = i < step ? "✓" : String(i + 1); });
  $$("#stepDots i").forEach((d, i) => d.classList.toggle("active", i === step));
  byId("onboardBack")!.style.visibility = step === 0 ? "hidden" : "visible";
  byId("onboardNext")!.textContent = ["Get started", prefs.calendarConnected ? "Continue" : "Skip for now", "Looks good", "Continue", "Start using Timo"][step] ?? "Continue";
  byId("skipOnboarding")!.style.visibility = step === 4 ? "hidden" : "visible";
}

export function showOnboarding(prefs: Preferences, at = 0) {
  step = at;
  // Reflect saved prefs into chips
  $$("#dayChips .preference-chip").forEach((c, i) => c.classList.toggle("active", prefs.workDays.includes((i + 1) % 7)));
  $$("#focusChips .preference-chip").forEach((c) => c.classList.toggle("active", c.textContent!.trim().toLowerCase().startsWith(prefs.focusWindow === "none" ? "no" : prefs.focusWindow)));
  // The hours were printed text - "9:00 AM", "5:30 PM" - so everyone was told they could set
  // them and nobody could. They are fields now, filled from what is saved.
  const startEl = byId<HTMLInputElement>("onboardStart"), endEl = byId<HTMLInputElement>("onboardEnd");
  if (startEl && prefs.workStart) startEl.value = prefs.workStart;
  if (endEl && prefs.workEnd) endEl.value = prefs.workEnd;
  const cc = byId("connectCalendar");
  if (cc) {
    cc.classList.toggle("connected", prefs.calendarConnected);
    byId("connectState")!.textContent = prefs.calendarConnected ? "✓ Connected" : "Connect →";
    byId("connectDescription")!.textContent = prefs.calendarConnected ? "Primary calendar · synced" : "Connect your primary calendar";
  }
  render(prefs);
  const host = byId("aiConnectSetup"); if (host) renderConnections(host, userNameForConnections, true);
  const el = byId("onboarding")!;
  el.classList.add("show");
  el.setAttribute("aria-hidden", "false");
}

function readPrefs(): Partial<Preferences> {
  const workDays = $$("#dayChips .preference-chip").map((c, i) => (c.classList.contains("active") ? (i + 1) % 7 : -1)).filter((d) => d >= 0);
  const focusText = $$("#focusChips .preference-chip").find((c) => c.classList.contains("active"))?.textContent?.trim().toLowerCase() ?? "morning";
  const focusWindow: Preferences["focusWindow"] = focusText.startsWith("after") ? "afternoon" : focusText.startsWith("no") ? "none" : "morning";
  const workStart = byId<HTMLInputElement>("onboardStart")?.value || "09:00";
  const workEnd = byId<HTMLInputElement>("onboardEnd")?.value || "17:30";
  // A day that ends before it starts would make every scheduling pass find no time at all.
  const valid = workEnd > workStart;
  const err = byId("onboardHoursError"); if (err) err.hidden = valid;
  return valid ? { workDays, focusWindow, workStart, workEnd } : { workDays, focusWindow };
}

export function setupOnboarding(getPrefs: () => Preferences) {
  const finish = async () => {
    const heard = document.querySelector<HTMLElement>("#heardChips [aria-checked='true']")?.dataset.heard;
    if (heard) sendSelfReport(heard);
    await savePreferences({ ...readPrefs(), onboarded: true });
    const el = byId("onboarding")!;
    el.classList.remove("show");
    el.setAttribute("aria-hidden", "true");
    toast("Welcome to Timo");
  };
  on(byId("onboardNext"), "click", async () => { if (step < 4) { if (step === 2) await savePreferences(readPrefs()); step++; render(getPrefs()); } else await finish(); });
  on(byId("onboardBack"), "click", () => { if (step > 0) { step--; render(getPrefs()); } });
  on(byId("skipOnboarding"), "click", finish);
  on(byId("connectCalendar"), "click", () => void connectCalendar());
  $$("#dayChips .preference-chip").forEach((b) => on(b, "click", () => b.classList.toggle("active")));
  $$("#focusChips .preference-chip").forEach((b) => on(b, "click", () => { $$("#focusChips .preference-chip").forEach((x) => x.classList.remove("active")); b.classList.add("active"); }));
  // One answer at most; tapping the chosen one again clears it, because the question is optional.
  $$("#heardChips [data-heard]").forEach((c) => on(c, "click", () => {
    const was = c.getAttribute("aria-checked") === "true";
    $$("#heardChips [data-heard]").forEach((x) => { x.setAttribute("aria-checked", "false"); x.classList.remove("active"); });
    if (!was) { c.setAttribute("aria-checked", "true"); c.classList.add("active"); }
  }));
  on(byId("restartOnboarding"), "click", () => showOnboarding(getPrefs(), 0));
  on(byId("manageAiFromIntegrations"), "click", () => showOnboarding(getPrefs(), 3));
  on(byId("agentSetupButton"), "click", () => showOnboarding(getPrefs(), 3));
}
